import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Dev Login Configuration
 *
 * Only available in dev mode, for quick bypass of Auth0 OAuth login
 */
const DEV_LOGIN_CONFIG = {
  enabled: import.meta.env.DEV && import.meta.env.VITE_DEV_LOGIN_ENABLED === 'true',
  token: import.meta.env.VITE_DEV_LOGIN_TOKEN || '',
};

// Dev Login state storage key
const DEV_LOGIN_STORAGE_KEY = 'dev_login_token';

// State change listeners (for triggering React re-renders)
type DevLoginListener = (token: string | null) => void;
const listeners: Set<DevLoginListener> = new Set();

function notifyListeners() {
  const token = sessionStorage.getItem(DEV_LOGIN_STORAGE_KEY);
  listeners.forEach((listener) => listener(token));
}

/**
 * Subscribe to Dev Login state changes
 */
export function subscribeDevLogin(listener: DevLoginListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Check if currently in Dev Login state
 */
export function isDevLoginActive(): boolean {
  if (!DEV_LOGIN_CONFIG.enabled) return false;
  return !!sessionStorage.getItem(DEV_LOGIN_STORAGE_KEY);
}

/**
 * Get Dev Login Token (if logged in)
 */
export function getDevLoginToken(): string | null {
  if (!DEV_LOGIN_CONFIG.enabled) return null;
  return sessionStorage.getItem(DEV_LOGIN_STORAGE_KEY);
}

/**
 * Set Dev Login state
 */
export function setDevLoginToken(token: string | null): void {
  if (token) {
    sessionStorage.setItem(DEV_LOGIN_STORAGE_KEY, token);
  } else {
    sessionStorage.removeItem(DEV_LOGIN_STORAGE_KEY);
  }
  notifyListeners();
}

/**
 * Clear Dev Login state
 */
export function clearDevLogin(): void {
  sessionStorage.removeItem(DEV_LOGIN_STORAGE_KEY);
  notifyListeners();
}

/**
 * Hook: Subscribe to Dev Login state (triggers re-render)
 */
export function useDevLoginState(): { isActive: boolean; token: string | null } {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = subscribeDevLogin(() => forceUpdate({}));
    return unsubscribe;
  }, []);

  return {
    isActive: isDevLoginActive(),
    token: getDevLoginToken(),
  };
}

/**
 * DevLoginButton - Development mode quick login button
 *
 * Only shows when:
 * 1. import.meta.env.DEV === true (Vite dev mode)
 * 2. VITE_DEV_LOGIN_ENABLED === "true"
 *
 * On click:
 * 1. Store dev token in sessionStorage
 * 2. Refresh page to trigger re-authentication
 */
export function DevLoginButton({ onLogin }: { onLogin?: () => void }) {
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);

  // Don't render if not in dev mode or not enabled
  if (!DEV_LOGIN_CONFIG.enabled) {
    return null;
  }

  const handleDevLogin = () => {
    if (!DEV_LOGIN_CONFIG.token) {
      console.error('[DevLogin] VITE_DEV_LOGIN_TOKEN is not set');
      return;
    }

    setIsLoading(true);

    // Store dev token
    setDevLoginToken(DEV_LOGIN_CONFIG.token);

    console.log('[DevLogin] Dev login activated, reloading...');

    // Call callback or refresh page
    if (onLogin) {
      onLogin();
    } else {
      // Refresh page to reinitialize auth state
      window.location.href = '/';
    }
  };

  return (
    <div className="mt-6 border-t border-dashed border-amber-300 pt-6">
      <div className="mb-3 flex items-center justify-center gap-2 text-xs text-amber-600">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
        <span>{t('devLogin.badge')}</span>
      </div>
      <button
        onClick={handleDevLogin}
        disabled={isLoading}
        className="w-full rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 px-4 py-3 font-medium text-amber-700 transition-all hover:border-amber-500 hover:bg-amber-100 disabled:opacity-50"
      >
        {isLoading ? t('devLogin.logging') : t('devLogin.button')}
      </button>
      <p className="mt-2 text-center text-xs text-amber-500">{t('devLogin.hint')}</p>
    </div>
  );
}

export default DevLoginButton;
